import React, { useEffect, useState } from 'react'
import { Control, useFieldArray, useForm } from 'react-hook-form'

import { getErrorMessage } from 'utils'
import { useSnackbar } from 'contexts'
import { useAddDemoProducts } from 'hooks/api'
import type {
  AddedDemoProduct,
  DemoListItem,
  ProvisionDemoProductInput,
} from 'services/demosApi'

// Tooltip from MUI, NOT @repo/ui — that one is a 0-byte stub that resolves in
// tsc and crashes at runtime.
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { CheckIcon, CopyIcon } from 'lucide-react'

import { Alert, Button, Modal } from '@repo/ui'
import {
  EMPTY_PRODUCT_ROW,
  ProductPicker,
  useProductImport,
  type ProductPickerFormValues,
} from 'components/input/ProductPicker'

interface AddDemoProductsModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

const DEFAULTS: ProductPickerFormValues = { products: [] }

/**
 * VRG-369 — add products to an existing demo, on the Demos console.
 *
 * ## Why this replaced entering the demo's space
 *
 * "Add products" used to enter the demo's company space and land on the real
 * `ProductEditPage` with its Add modal open. That worked — VRG-369 seeded the
 * permission grant that made it possible — but it is the wrong shape for the job.
 * Demo provisioning captures exactly FOUR fields per product (title, sku,
 * description, images) because that is all a demo certificate renders; the full
 * editor's sections, integrations and validations are all noise against that, and
 * getting to them costs a space entry, a re-auth of context, and a trip back.
 *
 * So this modal is the same `ProductPicker` the create flow uses, pointed at a
 * demo that already exists. **"Go to space" survives as its own kebab item** for
 * the rarer case where an AE genuinely wants the full editor or Studio — the
 * capability is not removed, it is just no longer the only door.
 *
 * ## The success step is not a courtesy
 *
 * Every added product gets its own active certificate (see
 * `AddDemoProductsUseCase` for why), but the invite link the prospect already has
 * is pinned to the ONE certificate provisioning created — `?certificate_id=…`. A
 * product added after the send therefore does NOT appear there. Listing each new
 * product's own URL is the only honest ending: without it the AE is told
 * "3 products added" and has no way to show any of them.
 */
export const AddDemoProductsModal = ({ demo, open, onClose }: AddDemoProductsModalProps) => {
  const { open: notify } = useSnackbar()
  const { mutate: addProducts, isPending } = useAddDemoProducts()

  const [added, setAdded] = useState<AddedDemoProduct[] | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const { control, handleSubmit, watch, reset, setValue } = useForm<ProductPickerFormValues>({
    defaultValues: DEFAULTS,
  })
  const { fields, prepend, remove } = useFieldArray({ control, name: 'products' })

  const rows = watch('products')
  const productImport = useProductImport({ prepend, rows })
  const selectedCount = rows?.filter((row) => row.selected && row.title?.trim()).length ?? 0

  // Reset per opening. Without this the modal reopens holding the previous demo's
  // picks, which would then be added to a DIFFERENT company on one click.
  useEffect(() => {
    if (open) {
      reset(DEFAULTS)
      productImport.reset()
      setAdded(null)
      setCopied(null)
    }
    // `productImport.reset` is stable (useCallback with no deps); listing the
    // whole object here would re-run this on every keystroke in the import field
    // and wipe the picker mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset])

  const toggleProduct = (index: number) => {
    setValue(`products.${index}.selected`, !rows?.[index]?.selected, { shouldDirty: true })
  }

  const setAllProductsSelected = (selected: boolean) => {
    rows?.forEach((_, index) => {
      setValue(`products.${index}.selected`, selected, { shouldDirty: true })
    })
  }

  const onValid = (values: ProductPickerFormValues) => {
    if (!demo) return

    const products: ProvisionDemoProductInput[] = values.products
      // Selection is what adds a product, not presence in the list. An unticked
      // row is one the AE looked at and rejected, and it stays listed only so the
      // rejection is reversible.
      .filter((p) => p.selected && p.title.trim())
      .map((p) => ({
        title: p.title.trim(),
        sku: p.sku.trim() || undefined,
        images: p.images.length ? p.images : undefined,
        description: p.description.trim() || undefined,
        sourceUrl: p.sourceUrl.trim() || undefined,
      }))

    if (!products.length) return

    addProducts(
      { companyId: demo.companyId, products },
      {
        onSuccess: (data) => {
          setAdded(data.products)
        },
        onError: (error) => {
          notify({ type: 'error', title: getErrorMessage(error) })
        },
      },
    )
  }

  const handleCopy = (product: AddedDemoProduct) => {
    void navigator.clipboard.writeText(product.demoUrl)
    setCopied(product.certificateId)
    window.setTimeout(() => setCopied(null), 1500)
  }

  const isDone = added !== null

  return (
    <Modal
      title={isDone ? 'Products added' : 'Add products'}
      titleAdornment={
        demo && !isDone ? (
          <Typography variant="body3" color="text.secondary">
            {demo.companyName}
          </Typography>
        ) : undefined
      }
      open={open}
      onClose={onClose}
      sx={{ width: 720 }}
      actions={
        isDone ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onValid)}
              loading={isPending}
              disabled={selectedCount === 0}
            >
              {selectedCount > 1 ? `Add ${selectedCount} products` : 'Add product'}
            </Button>
          </>
        )
      }
    >
      {isDone ? (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Each product has its own certificate and its own link. The invite you
            already sent points at the demo's original certificate, so use the links
            below to show these.
          </Typography>

          <Stack spacing={2}>
            {added.map((product) => (
              <Stack
                key={product.certificateId}
                direction="row"
                spacing={3}
                alignItems="center"
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.primary[100]}`,
                  borderRadius: 2,
                  px: 3,
                  py: 2,
                })}
              >
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
                    {product.title}
                  </Typography>
                  <Typography
                    variant="body3"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {product.demoUrl}
                  </Typography>
                </Stack>
                <Tooltip title={copied === product.certificateId ? 'Copied' : 'Copy link'}>
                  <IconButton
                    aria-label={`Copy the link for ${product.title}`}
                    size="small"
                    onClick={() => handleCopy(product)}
                    sx={{ flexShrink: 0 }}
                  >
                    {copied === product.certificateId ? (
                      <CheckIcon strokeWidth={1} size={16} />
                    ) : (
                      <CopyIcon strokeWidth={1} size={16} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Paste a listing URL to pull a product from the prospect's site, or add
            one by hand. Each product you add gets its own certificate, so it has a
            page you can show.
          </Typography>

          {demo?.isPurged && (
            <Alert
              severity="warning"
              description="This demo has been purged. There is no company left to add products to."
            />
          )}

          <Box>
            <ProductPicker
              control={control as unknown as Control<ProductPickerFormValues>}
              fields={fields}
              rows={rows ?? []}
              onToggle={toggleProduct}
              onRemove={remove}
              onSetAllSelected={setAllProductsSelected}
              onAddManual={() => prepend(EMPTY_PRODUCT_ROW)}
              importUrl={productImport.importUrl}
              onImportUrlChange={productImport.onImportUrlChange}
              onImport={productImport.onImport}
              isImporting={productImport.isImporting}
              importError={productImport.importError}
              importNotice={productImport.importNotice}
              emptyText="No products picked yet. Paste a listing URL above, or add one by hand."
            />
          </Box>
        </Stack>
      )}
    </Modal>
  )
}
